import { useIsMobile } from '@/hooks/use-mobile';
import ScrollAnimation from '../ScrollAnimation';
import FinancialConstellation, { NodeData } from './FinancialConstellation';
import { useEffect, useState, lazy, Suspense } from 'react';

// Lazy load Dialog - only needed when a node is clicked
const Dialog = lazy(() => import('@/components/ui/dialog').then(m => ({ default: m.Dialog })));
const DialogContent = lazy(() => import('@/components/ui/dialog').then(m => ({ default: m.DialogContent })));
const DialogHeader = lazy(() => import('@/components/ui/dialog').then(m => ({ default: m.DialogHeader })));
const DialogTitle = lazy(() => import('@/components/ui/dialog').then(m => ({ default: m.DialogTitle })));

const HeroVisual = () => {
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(false);
  const [selectedNode, setSelectedNode] = useState<NodeData | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 400);
    return () => clearTimeout(timer);
  }, []);

  const handleNodeClick = (node: NodeData) => {
    setSelectedNode(node);
  };

  const renderDetails = (details: string) => {
    const parts = details.split('\n\n');
    return parts.map((block, i) => {
      const lines = block.split('\n');
      const isFormula = lines.some(l => l.includes('=') && !l.includes('. '));
      if (isFormula && i > 0) {
        return (
          <div key={i} className="mt-3 p-3 rounded-lg bg-muted/50 border border-border/50 font-mono text-sm leading-relaxed">
            {lines.map((line, j) => (
              <div key={j} className={line.includes('=') ? 'text-foreground' : 'text-muted-foreground font-sans font-medium mb-1'}>
                {line}
              </div>
            ))}
          </div>
        );
      }
      return <p key={i} className="text-muted-foreground leading-relaxed">{block}</p>;
    });
  };
  
  return (
    <>
      <ScrollAnimation className="md:w-1/2 stagger-4 w-full" direction="right">
        <div className="relative">
          {/* Soft glow behind card */}
          <div className="absolute -inset-3 bg-primary/10 rounded-3xl blur-2xl opacity-60" />
          
          <div className="relative rounded-2xl border border-border/60 bg-card shadow-lg overflow-hidden">
            <div 
              className="relative overflow-hidden bg-gradient-to-br from-primary via-primary/90 to-primary/70" 
              style={{ 
                minHeight: isMobile ? '200px' : '380px',
                maxHeight: isMobile ? '240px' : 'none',
              }}
            >
              {/* Grid overlay */}
              <div 
                className="absolute inset-0 opacity-[0.06]"
                style={{
                  backgroundImage: `
                    linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)
                  `,
                  backgroundSize: isMobile ? '20px 20px' : '28px 28px'
                }}
              />
              
              {isVisible && <FinancialConstellation onNodeClick={handleNodeClick} />}
              
              {/* Central label */}
              <div className="absolute inset-0 flex items-center justify-center z-[100] pointer-events-none">
                <div className="px-5 py-2.5 md:px-7 md:py-3.5 bg-background/95 backdrop-blur-md rounded-xl shadow-2xl border border-border/40">
                  <h2 className="text-sm md:text-lg font-bold text-foreground text-center leading-tight tracking-tight">
                    DCF Model
                  </h2>
                  <p className="text-[9px] md:text-xs text-muted-foreground text-center mt-0.5">
                    Visualization
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ScrollAnimation>

      {selectedNode && (
        <Suspense fallback={null}>
          <Dialog open={!!selectedNode} onOpenChange={(open) => !open && setSelectedNode(null)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <div className="inline-flex items-center gap-2 mb-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: selectedNode?.color }}
                  />
                  <DialogTitle className="text-xl">{selectedNode?.label}</DialogTitle>
                </div>
                <div className="space-y-2">
                  {renderDetails(selectedNode?.details || '')}
                </div>
              </DialogHeader>
            </DialogContent>
          </Dialog>
        </Suspense>
      )}
    </>
  );
};

export default HeroVisual;
